import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Form, Image, Modal, Spinner } from 'react-bootstrap';
import * as Yup from 'yup';
import { Formik, Form as FormMik } from 'formik';
// eslint-disable-next-line import/no-duplicates
import { format } from 'date-fns';
import { useToast } from '../hooks/toast';
import api from '../services/apiClient';
import { unescapeHtml } from '../services/function';
import { formatLocalDateTime } from '../utils/dateTime';

import MessageContainer from '../components/Message/MessageContainer';

import { Message, MessageGroup, MessageGeneric } from '../interfaces/IMessage';

import FiltroIcon from '../assets/icon/filtro.svg';
import FiltroHoverIcon from '../assets/icon/filtroHover.svg';
import iconSearch from '../assets/icon/search.svg';
import TimerBrancoIcon from '../assets/icon/timerBranco.svg';
import TimerPretoIcon from '../assets/icon/timerPreto.svg';
import TimerVerdeIcon from '../assets/icon/timerVerde.svg';
import TimerAmareloIcon from '../assets/icon/timerAmarelo.svg';
import TimerVermelhoIcon from '../assets/icon/timerVermelho.svg';
import ButtonDotsIcon from '../assets/icon/buttonDots.svg';

interface DataRow {
  tipo: number;
  seqAtendimento: number;
  nomeCliente: string;
  emailCliente: string;
  nomeClinica: string;
  nomeProduto: string;
  duvida: string;
  dtAtendimentoCriado: string;
  nomeDepartamento: string;
  nomeStatusAtendimento: string;
  nomeUsuario: string;
  seqStatusAtendimento: number;
  statusTempoAtendimento: number;
}

interface Messages {
  seqAtendimento: number;
  messages: Message[];
}

interface MessagesGroup {
  seqGrupoConversa: number;
  messages: MessageGroup[];
}

interface Users {
  seqUsuario: number;
  nome: string;
}

interface FormField {
  periodoInicial: string;
  periodoFinal: string;
  seqUsuario: string;
  nomeCliente: string;
  nomeClinica: string;
  produto: string;
  conteudoMensagem: string;
  atendimentoCliente: boolean;
}

const dataFormatada = format(new Date(), 'yyyy-MM-dd');

const initialValues: FormField = {
  periodoInicial: dataFormatada,
  periodoFinal: dataFormatada,
  seqUsuario: '0',
  nomeCliente: '',
  nomeClinica: '',
  produto: '',
  conteudoMensagem: '',
  atendimentoCliente: true,
};

const ChatSupervisor: React.FC<any> = (): any => {
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = React.useState<DataRow[]>([] as DataRow[]);
  const [dadosChat, setDadosChat] = React.useState<DataRow>({} as DataRow);
  const [historicoConversa, setHistoricoConversa] = useState<Messages>(
    {} as Messages
  );
  const [rowSelected, setRowSelected] = useState(0);
  const [exibeFiltro, setExibeFiltro] = useState<boolean>(false);
  const [usuario, setUsuario] = useState<Users[]>([]);
  const [historicoConversaGrupo, setHistoricoConversaGrupo] =
    useState<MessagesGroup>({} as MessagesGroup);
  const bntPesquisaChat = useRef<HTMLButtonElement>(null);
  const [idInterval, setIdInterval] = useState<number>(0);
  const [exibeMsgSemPermissao, setExibeMsgSemPermissao] = useState(true);
  const timeInterval = 5000;
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [searchTextMessage, setSearchTextMessage] = useState('');
  const [dataTexto, setDataTexto] = useState('');

  /** ALTERAÇÃO: Adicionado useRef para armazenar a referência ao setValues do Formik, permitindo atualizar os valores do form dinamicamente após o mount. Motivo: Precisamos carregar os filtros salvos no localStorage e aplicá-los ao Formik após o componente montar, sem recriar o Formik. */
  const formikRef = useRef<any>(null);
  const rowSelectedRef = useRef(0);

  const timerIcon = (statusTempoAtend: number): string => {
    if (statusTempoAtend === 1) return TimerVerdeIcon;
    if (statusTempoAtend === 2) return TimerAmareloIcon;
    if (statusTempoAtend === 3) return TimerVermelhoIcon;

    return TimerPretoIcon;
  };

  const rowColor = (statusTempoAtend: number): string => {
    if (statusTempoAtend === 1) return '#B5EEB0';
    if (statusTempoAtend === 2) return '#F1F2C3';
    if (statusTempoAtend === 3) return '#FFB9B9';

    return '#fff';
  };

  const handleRowClicked = (row: any): any => {
    if (row.seqAtendimento !== dadosChat.seqAtendimento)
      setExibeMsgSemPermissao(true);
    else setExibeMsgSemPermissao(false);

    const updatedData = data.map((item) => {
      if (row.seqAtendimento !== item.seqAtendimento) {
        return {
          ...item,
          toggleSelected: false,
        };
      }

      setDadosChat(item);

      return {
        ...item,
        toggleSelected: true,
      };
    });

    const token = localStorage.getItem('@LiveChat:token');

    if (row.tipo === 1) {
      api
        .get(`ChatSupervisor/${row.seqAtendimento}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          try {
            const { success } = response.data;

            if (success) {
              const msg: Messages = {
                messages: response.data.data,
                seqAtendimento: row.seqAtendimento,
              };
              setHistoricoConversa(msg);
              setHistoricoConversaGrupo({} as MessagesGroup);
            }
          } catch (error) {
            setHistoricoConversa({} as Messages);
            setHistoricoConversaGrupo({} as MessagesGroup);

            // Não exibe a mensagem de 5 em 5 segundos
            if (exibeMsgSemPermissao) {
              addToast({
                title: 'Atenção',
                description: 'Você não tem permissão para ler esse chat.',
                type: 'warning',
              });

              setExibeMsgSemPermissao(false);
            }
          }
        });
    } else {
      api
        .get(`api/MensagemGrupo/${row.seqAtendimento}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const msg: MessagesGroup = {
            messages: response.data.data,
            seqGrupoConversa: row.seqAtendimento,
          };
          setHistoricoConversaGrupo(msg);
          setHistoricoConversa({} as Messages);
        });
    }
  };

  const validationSchema = Yup.object().shape({
    periodoInicial: Yup.date().max(
      Yup.ref('periodoFinal'),
      'Data Inicial inválida.'
    ),
    periodoFinal: Yup.date().min(
      Yup.ref('periodoInicial'),
      'Data Inicial inválida.'
    ),
  });

  const getHistoricoChat = (
    dataFilter: FormField,
    options?: { silent?: boolean }
  ): void => {
    const token = localStorage.getItem('@LiveChat:token');

    if (!options?.silent) setIsLoading(true);

    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    const param = {
      ...dataFilter,
      periodoInicial:
        dataFilter.periodoInicial === '' ? null : dataFilter.periodoInicial,
      periodoFinal:
        dataFilter.periodoFinal === '' ? null : dataFilter.periodoFinal,
      atendimentoCliente: true,
      nomeClinica:
        dataFilter.nomeClinica === '' ? '' : `%${dataFilter.nomeClinica}%`,
      nomeCliente:
        dataFilter.nomeCliente === '' ? '' : `%${dataFilter.nomeCliente}%`,
      produto: dataFilter.produto === '' ? '' : `%${dataFilter.produto}%`,
      conteudoMensagem:
        dataFilter.conteudoMensagem === ''
          ? ''
          : `%${dataFilter.conteudoMensagem}%`,
    };
    api
      .post('/ChatSupervisor', param, {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortControllerRef.current.signal,
      })
      .then((response) => {
        const { data: dataClients } = response.data;
        if (dataClients !== undefined) {
          setData(dataClients);
          if (!options?.silent) setIsLoading(false);
          /** ALTERAÇÃO: Após carregar os dados da tabela, se houver um rowSelected salvo, encontre a row correspondente e chame handleRowClicked para restaurar a conversa selecionada. Motivo: Para restaurar não só os filtros, mas também o registro selecionado ao recarregar a página. */
          if (rowSelectedRef.current) {
            const selectedRow = dataClients.find(
              (row: DataRow) => row.seqAtendimento === rowSelectedRef.current
            );
            if (selectedRow) {
              handleRowClicked(selectedRow);
            }
          }
        }
      });

    /** ALTERAÇÃO: Salvar os filtros no localStorage dentro de getHistoricoChat. Motivo: Como a maioria das alterações de filtro chama getHistoricoChat imediatamente, isso persiste as mudanças. Incluído exibeFiltro para persistir o estado do filtro expandido. */
    localStorage.setItem(
      '@LiveChat:chatSupervisorFilters',
      JSON.stringify({
        filters: dataFilter,
        rowSelected,
        exibeFiltro,
      })
    );
  };

  // Cleanup function to cancel any pending request when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const updateDataAutomatic = (): any => {
    const filters: FormField =
      formikRef.current && formikRef.current.values
        ? formikRef.current.values
        : initialValues;

    getHistoricoChat(filters, { silent: true });
  };

  useEffect(() => {
    const id = setInterval(updateDataAutomatic, timeInterval);
    setIdInterval(Number(id));

    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    rowSelectedRef.current = rowSelected;
  }, [rowSelected]);

  useEffect(() => {
    const token = localStorage.getItem('@LiveChat:token');

    /** ALTERAÇÃO: Removido o getHistoricoChat(initialValues) daqui, pois agora ele é chamado após carregar os filtros salvos no useEffect abaixo. Motivo: Garantir que os filtros salvos sejam aplicados antes de buscar os dados iniciais. */
    api
      .get('/api/user', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const { data: users } = response.data;
        setUsuario(users);
      });

    return () => {
      clearInterval(idInterval);
    };
  }, []);

  /** ALTERAÇÃO: Adicionado useEffect para carregar os filtros e rowSelected do localStorage ao montar o componente. Motivo: Restaurar o estado anterior (filtros e seleção) quando o usuário retorna à página. Adicionado carregamento de exibeFiltro e lógica para mostrá-lo se nomeClinica, produto ou conteudoMensagem não for vazio. */
  useEffect(() => {
    const savedData = localStorage.getItem('@LiveChat:chatSupervisorFilters');
    if (savedData && formikRef.current) {
      const {
        filters,
        rowSelected: savedRowSelected,
        exibeFiltro: savedExibeFiltro,
      } = JSON.parse(savedData);
      formikRef.current.setValues(filters);
      setRowSelected(savedRowSelected || 0);
      setExibeFiltro(
        savedExibeFiltro ||
          filters.nomeClinica !== '' ||
          filters.produto !== '' ||
          filters.conteudoMensagem !== ''
      );
      setSearchTextMessage(filters.conteudoMensagem);
      getHistoricoChat(filters);
    } else {
      getHistoricoChat(initialValues);
    }
  }, []);

  useEffect(() => {
    if (data.length === 0 || data.length === undefined) return;

    const idParam = searchParams.get('id');
    const id = idParam ? Number(idParam) : 0;

    if (id > 0) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('id'); // Remove o parâmetro desejado
      setSearchParams(newParams); // Atualiza a URL com os novos parâmetros

      // Encontra o chat correspondente ao id
      const selectedChat = data.find((chat) => chat.seqAtendimento === id);

      // Define o seqAtendimento do chat selecionado, ou 0 se não encontrado
      const seqAtendimento = selectedChat?.seqAtendimento ?? 0;
      setRowSelected(seqAtendimento);

      // Chama a função handleRowClicked com o chat encontrado
      handleRowClicked(selectedChat);
    }
  }, [data]);

  return (
    <div className="d-flex flex-column">
      <h1 className="fw-bold ms-2">Histórico de Atendimento</h1>
      <div className="d-flex w-100">
        <Modal
          centered
          show={dataTexto.length > 0}
          onHide={() => setDataTexto('')}
        >
          <Modal.Body>{dataTexto}</Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setDataTexto('')}>
              Fechar
            </Button>
          </Modal.Footer>
        </Modal>
        <div className="me-2" style={{ width: '60%' }}>
          <section className="ms-2">
            <Formik
              innerRef={
                formikRef
              } /** ALTERAÇÃO: Adicionado innerRef para acessar as funções do Formik (como setValues) de fora. Motivo: Permitir atualizar os valores do form dinamicamente ao carregar do localStorage. */
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting, resetForm }) => {
                getHistoricoChat(values);
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                isSubmitting,
                resetForm,
              }) => (
                <FormMik>
                  <div className="d-flex justify-content-end">
                    <Button
                      className="p-1 border-0 inputBox"
                      style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        backgroundColor: exibeFiltro ? '#0075FF' : '#fff',
                        color: exibeFiltro ? '#fff' : '#000',
                      }}
                      onClick={() => {
                        const newExibe = !exibeFiltro;
                        setExibeFiltro(newExibe);
                        /** ALTERAÇÃO: Adicionado salvamento no localStorage ao alternar exibeFiltro. Motivo: Persistir o estado de exibição do filtro expandido. Usa formikRef para obter os valores atuais. */
                        localStorage.setItem(
                          '@LiveChat:chatSupervisorFilters',
                          JSON.stringify({
                            filters: formikRef.current
                              ? formikRef.current.values
                              : initialValues,
                            rowSelected,
                            exibeFiltro: newExibe,
                          })
                        );
                      }}
                    >
                      Mais filtros
                      <Image
                        src={exibeFiltro ? FiltroHoverIcon : FiltroIcon}
                        alt="FilterIcon"
                        className="px-2"
                      />
                    </Button>
                  </div>
                  <div className="d-flex justify-content-center align-items-center">
                    <Form.Group className="p-0">
                      <Form.Label
                        htmlFor="inputPeriodoInicial"
                        className="mb-0"
                      >
                        Data Inicial
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="periodoInicial"
                        max="2999-12-31"
                        className={
                          (touched.periodoInicial || touched.periodoFinal) &&
                          errors.periodoFinal
                            ? 'is-invalid'
                            : 'p-0 inputBox'
                        }
                        onChange={(e) => {
                          handleChange(e);
                          getHistoricoChat({
                            ...values,
                            periodoInicial: e.target.value,
                          });
                          /** ALTERAÇÃO: Corrigido para values.periodoInicial em vez de initialValues.periodoInicial, que era um erro no código original. Motivo: initialValues é constante e não deve ser mutado; usar values do Formik. */
                        }}
                        onBlur={handleBlur}
                        value={values.periodoInicial}
                      />
                      {touched.periodoFinal && errors.periodoFinal ? (
                        <div className="invalid-feedback">
                          {errors.periodoFinal}
                        </div>
                      ) : null}
                    </Form.Group>

                    <Form.Group className="p-0 ms-2">
                      <Form.Label htmlFor="inputPeriodoFinal" className="mb-0">
                        Data Final
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="periodoFinal"
                        max="2999-12-31"
                        className="p-0 inputBox"
                        onChange={(e) => {
                          handleChange(e);
                          getHistoricoChat({
                            ...values,
                            periodoFinal: e.target.value,
                          });
                          /** ALTERAÇÃO: Corrigido para values.periodoFinal em vez de initialValues.periodoFinal, que era um erro no código original. Motivo: initialValues é constante e não deve ser mutado; usar values do Formik. */
                        }}
                        onBlur={handleBlur}
                        value={values.periodoFinal}
                      />
                    </Form.Group>

                    <Form.Group className="ms-2 w-50">
                      <Form.Label className="mb-0">Usuário</Form.Label>
                      <Form.Select
                        size="sm"
                        name="seqUsuario"
                        onChange={(e) => {
                          handleChange(e);
                          getHistoricoChat({
                            ...values,
                            seqUsuario: e.target.value,
                          });
                        }}
                        onBlur={handleBlur}
                        value={values.seqUsuario}
                        className="inputBox"
                      >
                        <option value={0}>Todos</option>
                        {usuario.map((user) => (
                          <option key={user.seqUsuario} value={user.seqUsuario}>
                            {user.nome}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="ms-2 w-50">
                      <Form.Label>Nome do cliente</Form.Label>
                      <Form.Control
                        type="text"
                        name="nomeCliente"
                        onChange={(e) => {
                          handleChange(e);
                          getHistoricoChat({
                            ...values,
                            nomeCliente: e.target.value,
                          });
                        }}
                        value={values.nomeCliente}
                        className="inputBox"
                      />
                    </Form.Group>
                  </div>
                  <div
                    className={`d-flex align-items-end ${
                      exibeFiltro ? '' : 'd-none'
                    }`}
                  >
                    <Form.Group className="ms-2 w-50">
                      <Form.Label>Nome da empresa</Form.Label>
                      <Form.Control
                        type="text"
                        name="nomeClinica"
                        className="mb-2 inputBox"
                        onChange={(e) => {
                          handleChange(e);
                          getHistoricoChat({
                            ...values,
                            nomeClinica: e.target.value,
                          });
                        }}
                        value={values.nomeClinica}
                      />
                    </Form.Group>
                    <Form.Group className="ms-2 w-50">
                      <Form.Label>Número de Contrato</Form.Label>
                      <Form.Control
                        type="text"
                        name="produto"
                        className="mb-2 inputBox"
                        value={values.produto}
                        onChange={(e) => {
                          handleChange(e);
                          getHistoricoChat({
                            ...values,
                            produto: e.target.value,
                          });
                        }}
                      />
                    </Form.Group>
                    <Form.Group className="ms-2 w-50">
                      <Form.Label>Conteúdo da Mensagem</Form.Label>
                      <Form.Control
                        type="text"
                        name="conteudoMensagem"
                        className="mb-2 inputBox"
                        value={values.conteudoMensagem}
                        onChange={(e) => {
                          handleChange(e);
                          setSearchTextMessage(e.target.value);
                          /** ALTERAÇÃO: Adicionado salvamento no localStorage no onChange de conteudoMensagem. Motivo: Como este campo não chama getHistoricoChat automaticamente, persistir mudanças imediatas para restaurar ao retornar. Usa {...values, ...} pois values ainda não está atualizado. */
                          localStorage.setItem(
                            '@LiveChat:chatSupervisorFilters',
                            JSON.stringify({
                              filters: {
                                ...values,
                                conteudoMensagem: e.target.value,
                              },
                              rowSelected,
                              exibeFiltro,
                            })
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            getHistoricoChat(values);
                          }
                        }}
                      />
                    </Form.Group>
                    <Button
                      className="ms-3 mb-2"
                      style={{ height: '2.75rem' }}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onClick={() => {
                        getHistoricoChat(values);
                      }}
                    >
                      <Image
                        src={iconSearch}
                        alt="search"
                        style={{ height: '1.75rem', width: '1.75rem' }}
                      />
                    </Button>
                  </div>
                </FormMik>
              )}
            </Formik>
          </section>
          <section
            className="card position-static ms-2 mt-3  overflow-auto position-relative"
            style={{
              borderRadius: '0.68rem',
              height: `Calc(100vh - ${exibeFiltro ? '27rem' : '22rem'})`,
              boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
            }}
          >
            {/* Spinner overlay */}
            {isLoading && ( // Substitua isLoading pela sua variável booleana
              <div
                className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1030,
                }}
              >
                <Spinner
                  style={{ width: '3rem', height: '3rem' }}
                  animation="border"
                  variant="primary"
                />
              </div>
            )}
            <table className="table table-nowrap mb-0 overflow-scroll table-sm w-auto">
              <thead
                className=" fw-light text-dark"
                style={{
                  backgroundColor: '#D9D9D9',
                  borderRadius: '0.68rem!important',
                  fontSize: '1.25rem',
                  position: 'sticky',
                  top: '0',
                }}
              >
                <tr
                  style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                  }}
                >
                  <th className=""> </th>
                  <th className="" style={{ width: '9rem' }}>
                    Dt. Atend.
                  </th>
                  <th className="">Nome</th>
                  <th className="">Operador</th>
                  <th className="">Produto</th>
                  <th className="">Dúvida</th>
                  <th className="">Email</th>
                  <th className="text-nowrap">Empresa / Cidade / Estado</th>
                  <th className="">Departamento</th>
                  <th className="text-nowrap">Status Atendimento</th>
                </tr>
              </thead>
              <tbody
                className="text-dark"
                style={{ borderColor: 'transparent', fontSize: '1.25rem' }}
              >
                {data.map((msg) => (
                  <tr
                    key={msg.seqAtendimento}
                    onClick={() => {
                      /** ALTERAÇÃO: Adicionado salvamento no localStorage ao selecionar uma row. Motivo: Garantir que o rowSelected seja persistido imediatamente ao clicar, para restaurar na próxima visita. Incluído exibeFiltro. */
                      setRowSelected(msg.seqAtendimento);
                      localStorage.setItem(
                        '@LiveChat:chatSupervisorFilters',
                        JSON.stringify({
                          filters: formikRef.current
                            ? formikRef.current.values
                            : initialValues,
                          rowSelected: msg.seqAtendimento,
                          exibeFiltro,
                        })
                      );
                      handleRowClicked(msg);
                    }}
                    style={{
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      backgroundColor:
                        rowSelected === msg.seqAtendimento
                          ? '#0075FF'
                          : rowColor(msg.statusTempoAtendimento),
                      color:
                        rowSelected === msg.seqAtendimento ? '#FFF' : '#000',
                    }}
                  >
                    <td className=" ps-2 me-2">
                      <Image
                        src={
                          rowSelected === msg.seqAtendimento
                            ? TimerBrancoIcon
                            : timerIcon(msg.statusTempoAtendimento)
                        }
                        className="ms-2 me-1 col"
                        style={{
                          width: '1.25rem',
                          height: '1.25rem',
                        }}
                      />
                    </td>
                    <td className="text-nowrap me-2">
                      {formatLocalDateTime(
                        msg.dtAtendimentoCriado,
                        'dd/MM/yyyy HH:mm'
                      )}
                    </td>
                    <td className="text-nowrap pe-3 me-2">{msg.nomeCliente}</td>
                    <td className="text-nowrap pe-3 me-2">{msg.nomeUsuario}</td>
                    <td className="text-nowrap pe-3 me-2">{msg.nomeProduto}</td>
                    <td className="text-nowrap pe-3 me-2">
                      {' '}
                      {msg.duvida?.length > 40
                        ? unescapeHtml(msg.duvida.substring(0, 40))
                        : unescapeHtml(msg.duvida)}{' '}
                      {msg.duvida.length > 40 && (
                        <Image
                          style={{ cursor: 'pointer' }}
                          className="ms-1"
                          src={ButtonDotsIcon}
                          alt="Nome do Cliente"
                          onClick={() => setDataTexto(unescapeHtml(msg.duvida))}
                        />
                      )}
                    </td>
                    <td className="text-nowrap pe-3 me-2">
                      {msg.emailCliente}
                    </td>
                    <td className="text-nowrap pe-3 me-2">{msg.nomeClinica}</td>
                    <td className="text-nowrap pe-3 me-2">
                      {msg.nomeDepartamento}
                    </td>
                    <td className="text-nowrap">{msg.nomeStatusAtendimento}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
        <section
          className="ms-2 d-flex flex-column"
          style={{
            width: '40%',
            borderRadius: '0.68rem',
            backgroundColor: '#FFF',
            fontSize: '1.25rem',
            boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
            flex: 1,
          }}
        >
          <div
            className="d-flex align-items-center ps-3"
            style={{
              borderTopLeftRadius: '0.68rem',
              borderTopRightRadius: '0.68rem',
              borderColor: 'transparent',
              backgroundColor: '#1A1A1A',
              fontSize: '1.25rem',
            }}
          >
            <span
              style={{
                color: '#fff',
                fontSize: '1.58rem',
                marginLeft: '0.6rem',
              }}
            >
              {`Chat - ${
                dadosChat.nomeCliente === undefined ? '' : dadosChat.nomeCliente
              }`}
            </span>
          </div>
          <div
            className="d-flex flex-column px-1"
            style={{
              borderBottomLeftRadius: '0.68rem',
              borderBottomRightRadius: '0.68rem',
              flex: 1,
            }}
          >
            <MessageContainer
              messages={
                historicoConversa.seqAtendimento === undefined
                  ? undefined
                  : historicoConversa
              }
              messagesGroup={
                historicoConversaGrupo.seqGrupoConversa === undefined
                  ? undefined
                  : historicoConversaGrupo
              }
              readOnly
              searchTextMessage={searchTextMessage}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default ChatSupervisor;
